/**
 * Handles session expiry events by redirecting to login with a toast notification
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../shared/context';
import { SESSION_EXPIRED_EVENT } from '../../shared/api/axiosInstance';

export function SessionExpiryHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { showWarning } = useNotification();

  // The listener below is registered once; read the CURRENT location through a
  // ref so the captured return URL is the page the user was actually on when
  // the session expired, not the one that was mounted at subscribe time.
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    const handleSessionExpired = () => {
      // Skip toast and redirect on public auth routes
      const publicAuthRoutes = [
        '/login',
        '/register',
        '/auth/sso/callback',
        '/verify-email',
        '/registration-pending',
        '/forgot-password',
        '/reset-password',
        '/external/', // All external portal routes
        // Public payment pages (token-driven, no JWT). No trailing slash: the
        // credential moved to the URL fragment, so the paths are now `/pay` and
        // `/pay-installment` with no path segment after them.
        '/pay',
        '/sign', // Public contract signing page (credential in the fragment, no JWT)
      ];
      const { pathname } = locationRef.current;
      const isPublicAuthRoute = publicAuthRoutes.some(
        (route) => pathname === route || pathname.startsWith(route)
      );

      if (!isPublicAuthRoute) {
        showWarning(t('errors.sessionExpired'));
        // Remember where the session died — including query string and hash —
        // so signing back in returns the user to the same page, the same way
        // ProtectedRoute's guard does for a cold deep link.
        navigate('/login', { replace: true, state: { from: locationRef.current } });
      }
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [navigate, t, showWarning]);

  return null;
}
